// @ts-check
import Highcharts from 'highcharts';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import sum from 'lodash/sum';

import {Component} from 'panel';
import {abbreviateNumber3SigFigs, commaizeNumber, truncateMiddle} from '../../util';
import {registerMPElement} from '../../util/register-element';
import {getChartSegmentColorCombo, primaryTextColor} from '../../util/colors';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import mpFonts from 'common/stylesheets/mixins/fonts.json';

import template from './index.jade';

/** @typedef {import('./types').IChartDataItem} IChartDataItem */

export default class PieChart extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      /**
       * @type {{type: string, default: IChartDataItem[]}}
       */
      'chart-data': {type: `json`, default: null},
      'chart-options-override ': {type: `string`, default: ``, description: `Overriden option in for highcharts`},
      'disable-animation': {type: `boolean`, default: false, description: `Should animation be disabled`},
      'tooltip-show-label': {type: `boolean`, default: true, description: `Should tooltips show label`},
      'tooltip-show-percentage': {type: `boolean`, default: true, description: `Should tooltips show percentage`},
      'tooltip-show-value': {type: `boolean`, default: true, description: `Should tooltips show value`},
      size: {
        type: `number`,
        default: 0,
        description: `Size of the pie chart. Using wrapping element size if not specified`,
      },
    };
  }

  get config() {
    return {
      template,
    };
  }

  attributeChangedCallback(name) {
    // @ts-ignore
    super.attributeChangedCallback(...arguments);
    switch (name) {
      case `size`: {
        if (this.highcharts) {
          this.highcharts.setSize(this.attr(`size`), this.attr(`size`));
        }
        break;
      }
      case `chart-data`:
        this.chartData = this.attr(`chart-data`);
        break;
      default:
        break;
    }
  }

  dataLabelsFormatter() {
    return function() {
      const label = this.key;
      return `
        <div class="pie-segment-label">
          ${truncateMiddle(label, 30)}
        </div>
      `;
    };
  }

  _tooltipFormatter() {
    const showLabel = this.attr(`tooltip-show-label`);
    const showPercentage = this.attr(`tooltip-show-percentage`);
    const showValue = this.attr(`tooltip-show-value`);
    const formatPercent = (decimal) => {
      return Math.round(decimal * Math.pow(10, 2)) / Math.pow(10, 2) + `%`;
    };

    return function() {
      if (showLabel || showPercentage || showValue) {
        let label = this.key;
        if (this.point.tooltipTitleExtraData) {
          label += ` ${this.point.tooltipTitleExtraData}`;
        }
        const sections = [
          [
            {
              text: truncateMiddle(label, 45),
              type: TooltipItemType.Strong,
            },
          ],
          [
            {
              text: isNumber(this.y) ? commaizeNumber(this.y) : ``,
              type: TooltipItemType.Data,
            },
          ],
          [
            {
              text: isNumber(this.percentage) ? formatPercent(this.percentage) : ``,
              type: TooltipItemType.Data,
            },
            {
              text: `of total`,
              type: TooltipItemType.Text,
            },
          ],
        ];
        return `<mp-chart-tooltip title-color="${this.point.color}" sections='${JSON.stringify(sections)}'>`;
      } else {
        return false;
      }
    };
  }

  /**
    expect incoming data to be structured as
    [
      {
        'name': string,
        'value': number,
        'color': string,
      },
      ...
    ]
  */
  render() {
    if (!this.chart) {
      this.chart = document.createElement(`div`);
      this.el.appendChild(this.chart);
    }
    let size = this.attr(`size`);
    if (!size) {
      const computedStyle = window.getComputedStyle(this.parentElement);
      size = Math.min(parseFloat(computedStyle.height), parseFloat(computedStyle.width));
    }
    const defaultChartOptions = {
      chart: {
        height: size,
        renderTo: this.chart,
        type: `pie`,
        width: size,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px`,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        align: `center`,
        style: {
          color: primaryTextColor,
          fontSize: false,
          fontWeight: `bold`,
        },
        text: abbreviateNumber3SigFigs(this.total),
        verticalAlign: `middle`,
        y: 10,
      },
      plotOptions: {
        pie: {
          center: [`50%`, `50%`],
          dataLabels: {
            enabled: false,
            formatter: this.dataLabelsFormatter(),
          },
          shadow: false,
          size: `100%`,
          slicedOffset: 0,
        },
        series: {
          animation: this.attr(`disable-animation`) ? false : 1000,
        },
      },
      tooltip: {
        backgroundColor: `#fff`,
        padding: 0,
        borderRadius: 10,
        borderWidth: 0,
        formatter: this._tooltipFormatter(),
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          data: this.chartData,
          innerSize: `60%`,
        },
      ],
    };
    const chartOptions = merge(defaultChartOptions, this.getJSONAttribute(`chart-options-override`));
    this.highcharts = new Highcharts.Chart(chartOptions);
  }

  get chartData() {
    return this._chartData || [];
  }

  get total() {
    return this._total || 0;
  }

  set chartData(data) {
    if (!isArray(data) || isEmpty(data)) {
      return;
    }
    this._total = sum(data.map((item) => item.value));
    data = data.map((item, idx) => {
      return {
        ...item,
        value: +item.value,
        color: item.color || getChartSegmentColorCombo(idx, `pieChart`).primary, // assign default primary colors if there's none
        secondaryColor: item.secondaryColor || getChartSegmentColorCombo(idx, `pieChart`).secondary, // assign default secondary colors if there's none
        name: item.name,
        y: item.value,
      };
    });

    if (!isEqual(this._chartData, data)) {
      this._chartData = data;
      this.render();
    }
  }
}

registerMPElement(`mp-pie-chart`, PieChart);
